import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { APP_BASE, HINT_HOP, SIGNED_IN_HINT, isAppPath, legacyAppPath } from './lib/base';
import './index.css';

/**
 * One document, two applications.
 *
 * The server answers every path with this shell, so the decision of what to
 * run is made here: the messenger under `/app`, the public page at `/`. Each
 * is a dynamic import, so a visitor downloads one of them and never both.
 *
 * Everything in this file runs before the first paint and has to stay cheap.
 */

const { pathname, search, hash } = window.location;

/* -------------------------------------------------------------------------
   Redirects, before anything is rendered.
   ------------------------------------------------------------------------- */

/** Links shared before the app moved: `/join/<code>` → `/app/join/<code>`. */
const legacy = isAppPath(pathname) ? null : legacyAppPath(pathname);
if (legacy) {
  window.location.replace(`${legacy}${search}${hash}`);
}

/**
 * A returning visitor should not see the landing at all.
 *
 * `Landing` also asks the server, which is the answer that counts, but that is
 * a round trip and it happens after paint — long enough to show a sales page
 * to somebody who has been signed in for a month. This flag is written when a
 * session starts and cleared when it ends, so the common case is a redirect
 * that costs nothing.
 *
 * Being wrong is the interesting case. A session that expired while the flag
 * survived used to send a signed-out visitor to `/app`, where they met the
 * sign-in screen — the one thing `/` is supposed to spare them. So the hop is
 * marked, and `boot-app` sends them back to the landing when the server says
 * there is no session after all. They see the landing a beat late rather than
 * a login form they did not ask for.
 */
const redirectingHome =
  !legacy &&
  pathname === '/' &&
  (() => {
    try {
      return localStorage.getItem(SIGNED_IN_HINT) === '1';
    } catch {
      // Private mode, or storage denied. Fall through to the landing.
      return false;
    }
  })();

if (redirectingHome) {
  const target = new URL(`${APP_BASE}${search}${hash}`, window.location.origin);
  target.searchParams.set(HINT_HOP, '1');
  window.location.replace(target.toString());
}

/* -------------------------------------------------------------------------
   Mount.
   ------------------------------------------------------------------------- */

// A `location.replace` above does not stop this script, and rendering into a
// document that is on its way out wastes the main thread at the worst moment.
if (!legacy && !redirectingHome) {
  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html');
  const root = createRoot(container);

  if (isAppPath(pathname)) {
    void import('./boot-app').then(({ mountApp }) => mountApp(root));
  } else {
    // Set before the chunk is even requested. The app pins the document to the
    // viewport and forbids scrolling; a page of prose has to undo that, and
    // doing it on arrival of the chunk means a frame of unscrollable landing.
    document.documentElement.dataset.page = 'landing';
    void import('./landing/Landing').then(({ default: Landing }) => {
      root.render(
        <StrictMode>
          <Landing />
        </StrictMode>,
      );
    });
  }
}
